import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Vapi from '@vapi-ai/web';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Mic, MicOff, Phone, PhoneOff, Loader2, RefreshCw, AlertTriangle, Info, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useDebouncedCallback } from 'use-debounce';
import CallGuidance from '@/components/ui/CallGuidance';
import ReconnectionPrompt from '@/components/ui/ReconnectionPrompt';

// Define type for permission state
type PermissionState = 'granted' | 'prompt' | 'denied' | 'unknown';

// Connection state type
type ConnectionState = 'connecting' | 'connected' | 'weak' | 'disconnected';

// Enhanced session state interface
interface CallSessionState {
  isCallActive: boolean;
  isMuted: boolean;
  sessionId: string;
  timestamp: number;
  callStartTime: number;
  lastActiveTimestamp: number;
  disconnectionCount: number;
  estimatedProgress: number;
  connectionQuality: 'strong' | 'weak' | 'poor';
  assistantConfig: any;
  checkInInfo: any;
  employeeInfo: any;
  backgroundedAt?: number;
  reconnectionAttempts: number;
}

// Error types for better handling
enum ErrorType {
  TOKEN_EXPIRED = 'token_expired',
  TOKEN_INVALID = 'token_invalid',
  MISSING_TOKEN = 'missing_token',
  NETWORK_ERROR = 'network_error',
  API_ERROR = 'api_error',
  VAPI_ERROR = 'vapi_error',
  MICROPHONE_ERROR = 'microphone_error',
  UNKNOWN_ERROR = 'unknown_error'
}

interface VoiceWaveProps {
  volume: number;
  active: boolean;
  isMuted?: boolean;
}

// Voice Waveform Animation Component
const VoiceWave: React.FC<VoiceWaveProps> = ({ volume, active, isMuted = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [waveData, setWaveData] = useState<number[]>(Array(60).fill(0));
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hovered, setHovered] = useState(false);

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update wave data based on volume with smoother transitions
  useEffect(() => {
    if (!active) return;

    // Smoothing factor for volume changes
    const smoothingFactor = 0.3;

    // Add new volume value and remove oldest, with smoothing
    setWaveData(prev => {
      const newData = [...prev];
      // If muted, create very small movements
      if (isMuted) {
        newData.push(Math.random() * 0.05);
      } else {
        // Get the last amplitude value for smoothing
        const lastValue = newData[newData.length - 1] || 0;
        // Calculate new value with smoothing (weighted average)
        const targetValue = Math.max(0.1, volume * 1.8); // Enhance low volumes
        const smoothedValue = lastValue * (1 - smoothingFactor) + targetValue * smoothingFactor;
        newData.push(smoothedValue);
      }
      newData.shift();
      return newData;
    });
  }, [volume, active, isMuted]);

  // Animation function with improved visuals
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const render = () => {
      // Canvas setup with device pixel ratio for sharper rendering
      const dpr = window.devicePixelRatio || 1;
      canvas.width = dimensions.width * dpr;
      canvas.height = dimensions.height * dpr;
      
      // Scale canvas according to device pixel ratio
      ctx.scale(dpr, dpr);
      
      // Set actual canvas dimensions
      canvas.style.width = `${dimensions.width}px`;
      canvas.style.height = `${dimensions.height}px`;
      
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);
      
      if (!active) {
        // Draw inactive state - animated horizontal line with subtle wave
        const time = Date.now() / 1000;
        ctx.beginPath();
        
        // Draw a subtle wavy line when inactive
        for (let x = 0; x < dimensions.width; x++) {
          const waveHeight = Math.sin(x / 50 + time) * 2;
          const y = dimensions.height / 2 + waveHeight;
          
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        // Create gradient for inactive state
        const gradient = ctx.createLinearGradient(0, 0, dimensions.width, 0);
        gradient.addColorStop(0, 'rgba(100, 100, 240, 0.2)');
        gradient.addColorStop(0.5, 'rgba(100, 100, 240, 0.4)');
        gradient.addColorStop(1, 'rgba(100, 100, 240, 0.2)');
        
        ctx.lineWidth = 2;
        ctx.strokeStyle = gradient;
        ctx.stroke();
        
        animationRef.current = requestAnimationFrame(render);
        return;
      }

      // Draw two waveforms: a reflection effect
      const drawWave = (yOffset: number, amplitudeMultiplier: number, alpha: number) => {
        ctx.beginPath();
        const segmentWidth = dimensions.width / (waveData.length - 1);
        
        // Add some randomization to make it look more natural
        const jitter = isMuted ? 0.2 : 0.5;
        const time = Date.now() / 1000;
        
        waveData.forEach((amplitude, i) => {
          // Add subtle randomization and movement
          const randomFactor = Math.sin(i * 0.2 + time * 2) * jitter;
          const effectiveAmplitude = amplitude * (1 + randomFactor) * amplitudeMultiplier;
          
          const x = i * segmentWidth;
          // Use a combination of sine waves for more natural movement
          const y = yOffset + effectiveAmplitude * Math.sin(i / 3 + time) * 20;
          
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            // Use quadratic curves for smoother lines
            const prevX = (i - 1) * segmentWidth;
            const prevY = yOffset + waveData[i-1] * amplitudeMultiplier * Math.sin((i-1) / 3 + time) * 20;
            const cpX = (prevX + x) / 2;
            const cpY = (prevY + y) / 2;
            ctx.quadraticCurveTo(cpX, cpY, x, y);
          }
        });
        
        // Determine colors based on volume, muted state, and hover
        let primaryColor, secondaryColor;
        
        if (isMuted) {
          // Muted colors
          primaryColor = 'rgba(180, 180, 180, ' + alpha * 0.8 + ')';
          secondaryColor = 'rgba(140, 140, 140, ' + alpha * 0.4 + ')';
        } else if (hovered) {
          // Hovered state - more vibrant
          primaryColor = 'rgba(64, 96, 255, ' + alpha + ')';
          secondaryColor = 'rgba(96, 128, 255, ' + alpha * 0.7 + ')';
        } else {
          // Normal active state
          primaryColor = 'rgba(0, 112, 243, ' + alpha + ')';
          secondaryColor = 'rgba(64, 160, 255, ' + alpha * 0.7 + ')';
        }
        
        // Create gradient for active waveform
        const gradient = ctx.createLinearGradient(0, 0, dimensions.width, 0);
        gradient.addColorStop(0, secondaryColor);
        gradient.addColorStop(0.5, primaryColor);
        gradient.addColorStop(1, secondaryColor);
        
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = gradient;
        ctx.stroke();
      };
      
      // Draw main waveform
      drawWave(dimensions.height / 2, 1, 1.0);
      
      // Draw reflection (slightly offset and with reduced opacity)
      drawWave(dimensions.height / 2 + 5, 0.7, 0.4);
      
      animationRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [waveData, active, dimensions, isMuted, hovered]);

  return (
    <div 
      ref={containerRef}
      className="voice-wave-container w-full h-32 bg-background/50 rounded-xl backdrop-blur-sm relative overflow-hidden border transition-all duration-300"
      style={{
        boxShadow: active ? '0 0 15px rgba(0, 112, 243, 0.15)' : 'none',
        transform: hovered && active ? 'scale(1.01)' : 'scale(1)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <canvas 
        ref={canvasRef} 
        className="w-full h-full"
      />
      {active && (
        <div className="absolute bottom-3 right-3">
          <span className="px-2 py-1 bg-black/20 backdrop-blur-sm text-white/90 rounded-full text-xs font-semibold flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
            {isMuted ? 'Muted' : 'Active'}
          </span>
        </div>
      )}
    </div>
  );
};

// Enhanced error message component with retry functionality
const ErrorMessage = ({ 
  title, 
  message, 
  errorType, 
  retryFunction,
  details
}: { 
  title: string;
  message: string;
  errorType: ErrorType;
  retryFunction?: () => void;
  details?: string;
}) => {
  const [showDetails, setShowDetails] = useState(false);
  
  const getIcon = () => {
    switch (errorType) {
      case ErrorType.TOKEN_EXPIRED:
      case ErrorType.TOKEN_INVALID:
        return <AlertTriangle className="h-6 w-6 text-destructive" />;
      case ErrorType.NETWORK_ERROR:
        return <RefreshCw className="h-6 w-6 text-amber-500" />;
      default:
        return <AlertTriangle className="h-6 w-6 text-destructive" />;
    }
  };

  return (
    <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive">
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1">
          <h3 className="font-semibold mb-2">{title}</h3>
          <p>{message}</p>
          
          {details && (
            <div className="mt-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDetails(!showDetails)}
                className="p-0 h-auto text-xs flex items-center gap-1"
              >
                <Info className="h-3 w-3" />
                {showDetails ? 'Hide technical details' : 'Show technical details'}
              </Button>
              
              {showDetails && (
                <pre className="mt-2 text-xs bg-background/70 p-2 rounded overflow-auto max-h-32">
                  {details}
                </pre>
              )}
            </div>
          )}
          
          {retryFunction && (
            <Button 
              variant="outline"
              size="sm"
              onClick={retryFunction}
              className="mt-3 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" /> Try Again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main component
export default function CheckInToken() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [vapi, setVapi] = useState<any>(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [micPermissionState, setMicPermissionState] = useState<PermissionState>('unknown');
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("default");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [voiceActivity, setVoiceActivity] = useState(false);
  
  // Enhanced state for token validation
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [validationError, setValidationError] = useState<{
    type: ErrorType;
    message: string;
    details?: string;
  } | null>(null);
  
  const [checkInInfo, setCheckInInfo] = useState<any>(null);
  const [employeeInfo, setEmployeeInfo] = useState<any>(null);
  const [assistantConfig, setAssistantConfig] = useState<any>(null);
  
  // Add retry count to prevent infinite retries
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const MAX_RETRIES = 3;

  // Wake lock and connection monitoring state
  const [wakeLock, setWakeLock] = useState<WakeLockSentinel | null>(null);
  const [wakeLockSupported, setWakeLockSupported] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastActiveTimestamp, setLastActiveTimestamp] = useState<number>(0);
  const [disconnectionCount, setDisconnectionCount] = useState(0);
  const [callStartTime, setCallStartTime] = useState<number>(0);
  const [showReconnectionPrompt, setShowReconnectionPrompt] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectionData, setReconnectionData] = useState<any>(null);
  const [reconnectionReady, setReconnectionReady] = useState(false);

  // Wake lock utilities with enhanced error handling
  const acquireWakeLock = async (): Promise<boolean> => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // iOS doesn't support wake lock in any browser
    if (isIOS) {
      setWakeLockSupported(false);
      console.log('Wake lock not available on iOS');
      return false;
    }
    
    if (!('wakeLock' in navigator)) {
      setWakeLockSupported(false);
      console.log('Wake lock API not supported');
      return false;
    }
    
    try {
      const lock = await navigator.wakeLock.request('screen');
      setWakeLock(lock);
      setWakeLockSupported(true);
      
      // Handle wake lock release with retry logic
      lock.addEventListener('release', () => {
        console.log('Wake lock released');
        setWakeLock(null);
        
        // Attempt to re-acquire if call is still active
        if (isCallActive && !document.hidden) {
          setTimeout(() => {
            acquireWakeLock();
          }, 1000);
        }
      });
      
      console.log('Wake lock acquired successfully');
      return true;
    } catch (error: any) {
      console.log('Wake lock failed:', error);
      setWakeLockSupported(false);
      
      // Provide specific error feedback
      if (error.name === 'NotAllowedError') {
        console.log('Wake lock permission denied');
      } else if (error.name === 'AbortError') {
        console.log('Wake lock request aborted');
      }
      
      return false;
    }
  };

  const releaseWakeLock = () => {
    if (wakeLock) {
      wakeLock.release();
      setWakeLock(null);
    }
  };

  // Progress calculation
  const calculateCallProgress = (): number => {
    if (!callStartTime) return 0;
    const elapsed = Date.now() - callStartTime;
    const maxCallDuration = 5 * 60 * 1000; // 5 minutes
    return Math.min(elapsed / maxCallDuration, 0.95); // Max 95% to never show "complete"
  };

  const determineConnectionQuality = (): 'strong' | 'weak' | 'poor' => {
    const timeSinceActivity = Date.now() - lastActiveTimestamp;
    if (timeSinceActivity < 5000) return 'strong';
    if (timeSinceActivity < 15000) return 'weak';
    return 'poor';
  };

  // Enhanced session state management with mobile optimizations
  const saveEnhancedSessionState = useCallback((additionalData: Partial<CallSessionState> = {}) => {
    try {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      const enhancedState: CallSessionState = {
        isCallActive,
        isMuted,
        sessionId: sessionId || crypto.randomUUID(),
        timestamp: Date.now(),
        callStartTime: callStartTime || Date.now(),
        lastActiveTimestamp,
        disconnectionCount,
        estimatedProgress: calculateCallProgress(),
        connectionQuality: determineConnectionQuality(),
        assistantConfig,
        checkInInfo,
        employeeInfo,
        reconnectionAttempts: 0,
        ...additionalData
      };
      
      // Add platform-specific metadata
      const platformState = {
        ...enhancedState,
        platform: isIOS ? 'ios' : 'other',
        userAgent: navigator.userAgent,
        screenOrientation: screen?.orientation?.type || 'unknown',
        connectionType: (navigator as any)?.connection?.effectiveType || 'unknown'
      };
      
      localStorage.setItem('enhanced_call_state', JSON.stringify(platformState));
      
      // iOS-specific: Also save a minimal recovery state in case of aggressive backgrounding
      if (isIOS && isCallActive) {
        const minimalState = {
          sessionId: sessionId,
          timestamp: Date.now(),
          progress: calculateCallProgress(),
          token: token
        };
        localStorage.setItem('ios_minimal_recovery', JSON.stringify(minimalState));
      }
    } catch (error) {
      console.error('Failed to save session state:', error);
      // Continue without crashing - localStorage might be full or unavailable
    }
  }, [isCallActive, isMuted, sessionId, callStartTime, lastActiveTimestamp, disconnectionCount, assistantConfig, checkInInfo, employeeInfo, token]);

  // Debounced connection monitoring
  const debouncedConnectionCheck = useDebouncedCallback(
    (volume: number) => {
      if (volume > 0.05) {
        setLastActiveTimestamp(Date.now());
        setConnectionState('connected');
      }
    },
    500
  );

  // Pre-load reconnection data when connection weakens
  useEffect(() => {
    if (connectionState === 'weak' && isCallActive) {
      prepareReconnection();
    }
  }, [connectionState, isCallActive]);

  const prepareReconnection = async () => {
    try {
      // Pre-validate token
      const tokenResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/check-in-link-handler?token=${token}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      
      if (tokenResponse.ok) {
        const data = await tokenResponse.json();
        setReconnectionData(data);
        setReconnectionReady(true);
      }
    } catch (error) {
      console.error('Failed to prepare reconnection:', error);
    }
  };

  // Generate contextual message for reconnection
  const generateContextualMessage = (): string => {
    const progress = calculateCallProgress();
    const disconnections = disconnectionCount;
    
    if (disconnections === 0) {
      return "Hi again! I see we got disconnected briefly. Let me continue with your check-in right where we left off.";
    } else if (progress < 0.3) {
      return "Welcome back! We were just getting started with your check-in. Let's pick up where we left off.";
    } else if (progress < 0.7) {
      return "Hi! We got disconnected, but I remember where we were in your check-in. Let me continue from where we left off.";
    } else {
      return "Almost done! We got disconnected near the end of your check-in. Let me finish up the last few questions.";
    }
  };

  // Lightning-fast reconnection with mobile optimizations
  const lightningReconnect = async (): Promise<boolean> => {
    const startTime = Date.now();
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsReconnecting(true);
    
    try {
      // Mobile-specific pre-connection setup
      if (isIOS) {
        // iOS: Ensure we're in foreground and ready
        if (document.hidden) {
          console.log('iOS: Waiting for app to foreground before reconnection');
          await new Promise(resolve => {
            const checkForeground = () => {
              if (!document.hidden) {
                resolve(true);
              } else {
                setTimeout(checkForeground, 100);
              }
            };
            checkForeground();
          });
        }
      }
      
      // Use pre-loaded data if available, with timeout for mobile
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Reconnection timeout')), 8000)
      );
      
      const configPromise = reconnectionReady ? 
        Promise.resolve(reconnectionData) : 
        validateTokenForReconnection();
      
      const configData = await Promise.race([configPromise, timeoutPromise]);
      
      if (!configData?.assistantConfig) {
        throw new Error('No assistant configuration available');
      }
      
      // Create contextual assistant config
      const contextualConfig = {
        ...configData.assistantConfig,
        firstMessage: generateContextualMessage()
      };
      
      // Start call with recovery context and mobile-specific settings
      await vapi.start(contextualConfig.assistantId, {
        variableValues: {
          ...contextualConfig.variableValues,
          isReconnection: true,
          disconnectionCount: disconnectionCount + 1,
          estimatedProgress: calculateCallProgress(),
          platform: isIOS ? 'ios' : 'other',
          reconnectionAttempt: true
        }
      });
      
      const duration = Date.now() - startTime;
      console.log(`Reconnection completed in ${duration}ms on ${isIOS ? 'iOS' : 'other'}`);
      
      // Update state
      setDisconnectionCount(prev => prev + 1);
      setIsCallActive(true);
      setShowReconnectionPrompt(false);
      setLastActiveTimestamp(Date.now()); // Reset activity timestamp
      
      // Re-acquire wake lock on non-iOS platforms
      if (!isIOS && wakeLockSupported) {
        acquireWakeLock();
      }
      
      // Show success feedback
      toast({
        title: "Reconnected Successfully",
        description: `Connection restored in ${duration < 1000 ? '<1' : Math.round(duration/1000)} second${duration >= 2000 ? 's' : ''}`,
        variant: "default"
      });
      
      return true;
    } catch (error: any) {
      console.error('Lightning reconnect failed:', error);
      
      // Provide specific error feedback
      const errorMessage = error.message === 'Reconnection timeout' 
        ? 'Reconnection timed out. Please try again.' 
        : 'Could not restore your call. Please start over.';
      
      toast({
        title: "Reconnection Failed",
        description: errorMessage,
        variant: "destructive"
      });
      return false;
    } finally {
      setIsReconnecting(false);
    }
  };

  const validateTokenForReconnection = async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/check-in-link-handler?token=${token}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    );
    
    if (!response.ok) {
      throw new Error('Token validation failed');
    }
    
    return await response.json();
  };

  // Connection monitoring with platform-aware thresholds
  const monitorConnection = useCallback(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    const checkConnection = () => {
      if (!vapi || !isCallActive) {
        setConnectionState('disconnected');
        return;
      }
      
      const timeSinceActivity = Date.now() - lastActiveTimestamp;
      
      // Platform-specific thresholds
      const weakThreshold = isIOS ? 8000 : 15000;  // iOS: 8s, Others: 15s
      const disconnectedThreshold = isIOS ? 25000 : 45000;  // iOS: 25s, Others: 45s
      
      // Monitor volume levels as connection indicator
      if (volume > 0 || timeSinceActivity < 5000) {
        setConnectionState('connected');
        if (volume > 0) {
          setLastActiveTimestamp(Date.now());
        }
      } else if (timeSinceActivity < weakThreshold) {
        setConnectionState('weak');
      } else if (timeSinceActivity < disconnectedThreshold) {
        setConnectionState('disconnected');
      } else {
        // Show reconnection prompt after extended disconnection
        setConnectionState('disconnected');
        if (isCallActive && !showReconnectionPrompt) {
          setShowReconnectionPrompt(true);
        }
      }
    };
    
    const interval = setInterval(checkConnection, 2000);
    return () => clearInterval(interval);
  }, [vapi, isCallActive, volume, lastActiveTimestamp, showReconnectionPrompt]);

  // Start connection monitoring when call becomes active
  useEffect(() => {
    if (isCallActive) {
      return monitorConnection();
    }
  }, [isCallActive, monitorConnection]);

  // Periodically save session state during active calls
  useEffect(() => {
    if (!isCallActive) return;
    
    const interval = setInterval(() => {
      saveEnhancedSessionState();
    }, 10000); // Save every 10 seconds during active call
    
    return () => clearInterval(interval);
  }, [isCallActive, saveEnhancedSessionState]);

  // Connection health verification function
  const verifyConnectionHealth = async () => {
    // Quick connection verification with platform-specific logic
    const healthData = localStorage.getItem('connection_health');
    if (healthData) {
      const health = JSON.parse(healthData);
      const timeSinceCheck = Date.now() - health.timestamp;
      const isIOS = health.platform === 'ios' || /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // iOS has more aggressive timeouts
      const disconnectionThreshold = isIOS ? 8000 : 15000;
      
      if (timeSinceCheck > disconnectionThreshold && !health.callActive) {
        // Connection likely lost
        setConnectionState('disconnected');
        setShowReconnectionPrompt(true);
      } else if (isIOS && timeSinceCheck > 30000) {
        // iOS likely suspended the app - assume disconnection
        setConnectionState('disconnected');
        setShowReconnectionPrompt(true);
      }
    }
  };

  // Network change detection and handling
  useEffect(() => {
    const handleNetworkChange = () => {
      console.log('Network change detected');
      if (isCallActive) {
        // Save current state immediately on network change
        saveEnhancedSessionState({
          connectionQuality: 'poor' // Assume poor quality during network transition
        });
        
        // Set connection to weak temporarily
        setConnectionState('weak');
        
        // Show user feedback
        toast({
          title: "Network Change Detected",
          description: "Monitoring connection stability...",
          variant: "default"
        });
      }
    };

    // Listen for online/offline events
    window.addEventListener('online', handleNetworkChange);
    window.addEventListener('offline', handleNetworkChange);
    
    return () => {
      window.removeEventListener('online', handleNetworkChange);
      window.removeEventListener('offline', handleNetworkChange);
    };
  }, [isCallActive, saveEnhancedSessionState, toast]);

  // iOS-specific app lifecycle handling
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted && isCallActive) {
        // Page was restored from bfcache (iOS back/forward cache)
        console.log('iOS: Page restored from cache during active call');
        verifyConnectionHealth();
        
        // iOS likely suspended the call - offer reconnection
        setTimeout(() => {
          if (isCallActive && !showReconnectionPrompt) {
            setShowReconnectionPrompt(true);
          }
        }, 1000);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [isCallActive, showReconnectionPrompt, verifyConnectionHealth]);

  // Enhanced visibility change handler
  const handleAdvancedVisibilityChange = useCallback(() => {
    const now = Date.now();
    
    if (document.hidden && isCallActive) {
      console.log('App backgrounded during active call');
      
      // Save detailed state immediately
      saveEnhancedSessionState({
        backgroundedAt: now,
        connectionQuality: determineConnectionQuality()
      });
      
      // Start background monitoring
      startBackgroundMonitoring();
      
      toast({
        title: "Call Continues in Background",
        description: "Return to this screen if the call disconnects",
        variant: "default"
      });
      
    } else if (!document.hidden) {
      console.log('App foregrounded');
      
      // Check for interrupted call
      const savedState = localStorage.getItem('enhanced_call_state');
      if (savedState) {
        const state: CallSessionState = JSON.parse(savedState);
        const backgroundDuration = now - (state.backgroundedAt || now);
        
        console.log(`Was backgrounded for ${backgroundDuration}ms`);
        
        // Handle different background durations
        if (backgroundDuration > 15000 && !isCallActive && state.isCallActive) {
          // Likely disconnected - offer reconnection
          setShowReconnectionPrompt(true);
        } else if (backgroundDuration > 5000 && isCallActive) {
          // Still connected but might be weak - verify connection
          verifyConnectionHealth();
        }
      }
      
      stopBackgroundMonitoring();
    }
  }, [isCallActive, saveEnhancedSessionState]);

  const startBackgroundMonitoring = () => {
    // Monitor connection health in background with iOS-optimized intervals
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const monitorInterval = isIOS ? 1000 : 3000; // More frequent checks on iOS before suspension
    
    const monitor = setInterval(() => {
      if (document.hidden && isCallActive) {
        const connectionHealth = {
          timestamp: Date.now(),
          volume: volume,
          lastActivity: lastActiveTimestamp,
          callActive: vapi?.isCallActive || false,
          platform: isIOS ? 'ios' : 'other'
        };
        
        // Store health check
        localStorage.setItem('connection_health', JSON.stringify(connectionHealth));
      }
    }, monitorInterval);
    
    // Store interval reference
    (window as any).backgroundMonitor = monitor;
  };

  const stopBackgroundMonitoring = () => {
    if ((window as any).backgroundMonitor) {
      clearInterval((window as any).backgroundMonitor);
      delete (window as any).backgroundMonitor;
    }
  };

  // Multi-event background detection
  useEffect(() => {
    const events = [
      'visibilitychange',
      'pagehide',
      'pageshow',
      'focus',
      'blur',
      'beforeunload'
    ];
    
    const handleEvent = (event: Event) => {
      console.log(`Event: ${event.type}`);
      
      switch (event.type) {
        case 'visibilitychange':
          handleAdvancedVisibilityChange();
          break;
          
        case 'pagehide':
          if (isCallActive) {
            saveEnhancedSessionState({ 
              backgroundedAt: Date.now(),
              connectionQuality: 'poor' // Assume poor on page hide
            });
          }
          break;
          
        case 'pageshow':
          // Page restored from cache
          if (isCallActive) {
            verifyConnectionHealth();
          }
          break;
          
        case 'beforeunload':
          // User closing/refreshing
          if (isCallActive) {
            // Quick save before page unloads
            localStorage.setItem('call_interrupted_unload', JSON.stringify({
              timestamp: Date.now(),
              progress: calculateCallProgress(),
              sessionId: sessionId
            }));
          }
          break;
      }
    };
    
    events.forEach(event => {
      document.addEventListener(event, handleEvent);
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleEvent);
      });
    };
  }, [isCallActive, handleAdvancedVisibilityChange, sessionId]);

  // Enhanced session recovery on mount with mobile-specific handling
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const savedSession = localStorage.getItem('enhanced_call_state');
    const interruptedUnload = localStorage.getItem('call_interrupted_unload');
    const iosMinimalRecovery = localStorage.getItem('ios_minimal_recovery');
    
    // Primary recovery from enhanced state
    if (savedSession) {
      try {
        const session: CallSessionState = JSON.parse(savedSession);
        const sessionAge = Date.now() - session.timestamp;
        const maxAge = isIOS ? 3 * 60 * 1000 : 5 * 60 * 1000; // iOS: 3min, Others: 5min
        
        if (sessionAge < maxAge) {
          setSessionId(session.sessionId);
          setCallStartTime(session.callStartTime);
          setLastActiveTimestamp(session.lastActiveTimestamp);
          setDisconnectionCount(session.disconnectionCount);
          
          console.log(`Recovering session from ${Math.round(sessionAge/1000)}s ago on ${isIOS ? 'iOS' : 'other'}`);
          
          // If call was active, show reconnection prompt
          if (session.isCallActive) {
            setShowReconnectionPrompt(true);
          }
        } else {
          console.log(`Session too old (${Math.round(sessionAge/1000)}s), cleaning up`);
          localStorage.removeItem('enhanced_call_state');
        }
      } catch (error) {
        console.error('Error recovering enhanced session:', error);
        localStorage.removeItem('enhanced_call_state');
      }
    }
    
    // iOS-specific minimal recovery (fallback for aggressive backgrounding)
    if (isIOS && iosMinimalRecovery && !savedSession) {
      try {
        const minimalState = JSON.parse(iosMinimalRecovery);
        const recoveryAge = Date.now() - minimalState.timestamp;
        
        if (recoveryAge < 2 * 60 * 1000) { // 2 minutes for minimal recovery
          console.log('iOS: Using minimal recovery state');
          setSessionId(minimalState.sessionId);
          setShowReconnectionPrompt(true);
          
          toast({
            title: "Session Found",
            description: "Found a recent call session. Reconnect to continue?",
            variant: "default"
          });
        }
        localStorage.removeItem('ios_minimal_recovery');
      } catch (error) {
        console.error('Error recovering iOS minimal state:', error);
        localStorage.removeItem('ios_minimal_recovery');
      }
    }
    
    // Handle interrupted unload
    if (interruptedUnload) {
      try {
        const unloadData = JSON.parse(interruptedUnload);
        const unloadAge = Date.now() - unloadData.timestamp;
        const maxUnloadAge = isIOS ? 90 * 1000 : 2 * 60 * 1000; // iOS: 90s, Others: 2min
        
        if (unloadAge < maxUnloadAge) {
          console.log('Recovering from interrupted unload');
          setShowReconnectionPrompt(true);
        }
        localStorage.removeItem('call_interrupted_unload');
      } catch (error) {
        console.error('Error recovering from unload:', error);
        localStorage.removeItem('call_interrupted_unload');
      }
    }
  }, [toast]);

  // Audio device enumeration
  useEffect(() => {
    const enumerateDevices = async () => {
      if (!navigator.mediaDevices?.enumerateDevices) {
        console.warn('enumerateDevices() not supported.');
        return;
      }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(device => device.kind === 'audioinput');
        setAudioDevices(audioInputs);
        
        // Set default device if available
        if (audioInputs.length > 0 && !selectedAudioDevice) {
          // Find the first device with a non-empty ID
          const validDevice = audioInputs.find(device => device.deviceId && device.deviceId.trim() !== '');
          if (validDevice) {
            setSelectedAudioDevice(validDevice.deviceId);
          }
        }
      } catch (err) {
        console.error('Error enumerating devices:', err);
      }
    };

    enumerateDevices();
    // Listen for device changes
    navigator.mediaDevices?.addEventListener('devicechange', enumerateDevices);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', enumerateDevices);
  }, []);

  // Query microphone permission state on mount
  useEffect(() => {
    if (!isTokenValid) return;

    const queryMicPermission = async () => {
      if (navigator.permissions) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          setMicPermissionState(permissionStatus.state);
          permissionStatus.onchange = () => {
            setMicPermissionState(permissionStatus.state);
          };
        } catch (error) {
          console.error("Error querying microphone permission:", error);
          setMicPermissionState('unknown'); // Fallback state
        }
      } else {
        console.warn("Permissions API not supported, defaulting to prompt.");
        setMicPermissionState('prompt'); // Assume prompt if API not available
      }
    };

    queryMicPermission();
  }, [isTokenValid]);

  // Verify token validity by calling the edge function
  const validateToken = async (shouldRetry = false) => {
    if (shouldRetry) {
      setIsRetrying(true);
      setRetryCount(prev => prev + 1);
    } else {
      setIsValidating(true);
    }
    
    setValidationError(null);

    if (!token) {
      setIsValidating(false);
      setIsRetrying(false);
      setValidationError({
        type: ErrorType.MISSING_TOKEN,
        message: "No token provided in the URL"
      });
      return;
    }

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/check-in-link-handler?token=${token}`;
      
      console.log("Validating token at:", apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Log response details for debugging
      console.log("Token validation response status:", response.status);
      
      // Clone the response before parsing JSON to avoid consuming it
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error("Failed to parse JSON response:", jsonError);
        // Try to get text response for debugging
        const textResponse = await responseClone.text();
        console.log("Raw response:", textResponse.substring(0, 200));
        throw new Error("Invalid response format");
      }
      
      console.log("Token validation response:", data);

      if (!response.ok) {
        setIsValidating(false);
        setIsRetrying(false);
        setIsTokenValid(false);
        
        // Handle specific error codes from the edge function
        if (data.error === 'token_expired') {
          setValidationError({
            type: ErrorType.TOKEN_EXPIRED,
            message: "This check-in link has expired",
            details: JSON.stringify(data.details || {}, null, 2)
          });
        } else if (data.error === 'token_invalid') {
          setValidationError({
            type: ErrorType.TOKEN_INVALID,
            message: "This check-in link is invalid",
            details: JSON.stringify(data.details || {}, null, 2)
          });
        } else {
          setValidationError({
            type: ErrorType.API_ERROR,
            message: data.message || "Token verification failed",
            details: JSON.stringify(data.details || {}, null, 2)
          });
        }
        
        return;
      }

      // Token is valid
      setIsTokenValid(true);
      setCheckInInfo(data.checkIn);
      setEmployeeInfo(data.employee);
      setAssistantConfig(data.assistantConfig);
      
      console.log("Successfully validated token, assistant config:", data.assistantConfig);
    } catch (error: unknown) {
      console.error("Token validation error:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      
      setValidationError({
        type: ErrorType.NETWORK_ERROR,
        message: `Connection error: ${errorMessage}`,
        details: error instanceof Error ? error.stack : undefined
      });
      
      // Show toast for network errors
      toast({
        title: "Connection Error",
        description: "Failed to connect to the server. Please check your internet connection.",
        variant: "destructive"
      });
    } finally {
      setIsValidating(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    validateToken();
  }, [token]);

  // Initialize Vapi SDK
  useEffect(() => {
    if (!isTokenValid) return;

    try {
      const vapiInstance = new Vapi(import.meta.env.VITE_VAPI_PUBLIC_KEY || 'your-public-key');

      // Set up enhanced event listeners
      vapiInstance.on('call-start', () => {
        setIsCallActive(true);
        setIsStarting(false);
        setSessionId(prev => prev || crypto.randomUUID());
        setCallStartTime(Date.now());
        setLastActiveTimestamp(Date.now());
        setConnectionState('connected');
        console.log("Vapi call started successfully");
      });

      vapiInstance.on('call-end', () => {
        setIsCallActive(false);
        setVolume(0);
        setVoiceActivity(false);
        setConnectionState('disconnected');
        setShowReconnectionPrompt(false);
        console.log("Vapi call ended");
        
        // Clean up all session data
        localStorage.removeItem('enhanced_call_state');
        localStorage.removeItem('connection_health');
        localStorage.removeItem('ios_minimal_recovery');
        localStorage.removeItem('call_interrupted_unload');
        
        releaseWakeLock();
        setSessionId(null);
        setCallStartTime(0);
        setLastActiveTimestamp(0);
        setDisconnectionCount(0);
      });

      vapiInstance.on('volume-level', (level: number) => {
        setVolume(level);
        setVoiceActivity(level > 0.1);
        
        // Update connection state based on activity
        if (level > 0.05) {
          setLastActiveTimestamp(Date.now());
          setConnectionState('connected');
        }
        
        // Use debounced connection check
        debouncedConnectionCheck(level);
      });

      vapiInstance.on('error', (error: Error) => {
        console.error('Vapi error:', error);
        toast({
          title: "Voice Assistant Error",
          description: error.message || "An error occurred with the voice assistant",
          variant: "destructive"
        });
      });

      setVapi(vapiInstance);

      return () => {
        if (vapiInstance) {
          try {
            vapiInstance.stop();
          } catch (error) {
            console.error("Error stopping Vapi call:", error);
          }
        }
      };
    } catch (error) {
      console.error("Error initializing Vapi:", error);
      setValidationError({
        type: ErrorType.VAPI_ERROR,
        message: "Failed to initialize voice assistant",
        details: error instanceof Error ? error.message : String(error)
      });
    }
  }, [isTokenValid, selectedAudioDevice]);

  // Start call function
  const startCall = async (event?: React.MouseEvent | boolean) => {
    if (event && typeof event !== 'boolean') {
      event.preventDefault();
    }
    
    const isRecovery = event === true;

    if (!vapi || !assistantConfig || !assistantConfig.assistantId) {
      toast({
        title: "Configuration Error",
        description: "Missing assistant configuration. Please refresh the page and try again.",
        variant: "destructive"
      });
      return;
    }

    // 1. Check permission state FIRST
    if (micPermissionState === 'denied') {
      toast({
        title: "Microphone Access Denied",
        description: "Microphone access was previously denied. Please enable it in your browser settings.",
        variant: "destructive"
      });
      return;
    }

    // 2. Only request permission if state is 'prompt' or 'unknown' and not recovering
    let permissionJustGranted = false;
    if (!isRecovery && (micPermissionState === 'prompt' || micPermissionState === 'unknown')) {
      if (navigator.mediaDevices) {
        try {
          console.log("Requesting microphone permission...");
          // Fixed: Use ideal constraint instead of exact to prevent mobile issues
          const constraints = {
            audio: selectedAudioDevice && selectedAudioDevice !== 'default'
              ? { deviceId: selectedAudioDevice }  // Use ideal constraint, not exact
              : true
          };
          // This call triggers the prompt if state is 'prompt'
          await navigator.mediaDevices.getUserMedia(constraints);
          console.log("Microphone permission granted.");
          setMicPermissionState('granted'); // Update state
          permissionJustGranted = true;
        } catch (micError) {
          console.error("Microphone permission denied during request:", micError);
          setMicPermissionState('denied'); // Update state on denial
          setValidationError({
            type: ErrorType.MICROPHONE_ERROR,
            message: "Microphone access is required. Please allow microphone access and try again.",
            details: micError instanceof Error ? micError.message : String(micError)
          });
          toast({
            title: "Microphone Required",
            description: "Microphone access denied. Please allow access and try again.",
            variant: "destructive"
          });
          return;
        }
      } else {
        toast({
          title: "Microphone Not Supported",
          description: "Your browser does not support microphone access.",
          variant: "destructive"
        });
        return;
      }
    }

    // 3. Proceed to start if permission is granted (either now or previously) or recovering
    if (isRecovery || micPermissionState === 'granted' || permissionJustGranted) {
      setIsStarting(true);
      
      // Acquire wake lock before starting call (only for new calls, not recovery)
      if (!isRecovery) {
        const wakeLockAcquired = await acquireWakeLock();
        
        if (wakeLockAcquired) {
          toast({
            title: "Screen Protection Enabled",
            description: "Your screen will stay on during the call",
            variant: "default"
          });
        } else {
          toast({
            title: "Keep Screen Active",
            description: "Please don't lock your phone during the call",
            variant: "default"
          });
        }
      }
      
      try {
        console.log("Starting call with assistant ID:", assistantConfig.assistantId, 
          isRecovery ? "(Recovering session)" : "(New session)");
        
        await vapi.start(assistantConfig.assistantId, {
          variableValues: {
            ...assistantConfig.variableValues,
            isRecoveredSession: isRecovery
          }
        });

        if (isRecovery) {
          toast({
            title: "Session Recovered",
            description: "Your previous call session has been restored",
            variant: "default"
          });
        }
      } catch (error: unknown) {
        console.error('Error starting call:', error);
        if (error && typeof error === 'object' && 'response' in error) {
          console.error('Error response:', (error as any).response);
        }
        
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        
        toast({
          title: "Call Error",
          description: `Failed to start voice call: ${errorMessage}`,
          variant: "destructive"
        });
        setIsStarting(false);
      }
      // Note: setIsStarting(false) should happen in the call-start event listener
    } else {
      // This case should ideally not be reached if logic is correct
      console.warn("Could not start call due to unexpected permission state:", micPermissionState);
      toast({
        title: "Cannot Start Call",
        description: "Could not verify microphone permissions.",
        variant: "destructive"
      });
    }
  };

  // Stop call function
  const stopCall = () => {
    if (!vapi) return;
    try {
      vapi.stop();
      releaseWakeLock(); // Release wake lock when call ends
    } catch (error) {
      console.error("Error stopping call:", error);
      toast({
        title: "Call Error",
        description: "Error ending the call. The session may have already ended.",
        variant: "destructive"
      });
    }
  };

  // Cleanup on unmount with mobile-specific handling
  useEffect(() => {
    return () => {
      releaseWakeLock();
      stopBackgroundMonitoring();
      
      // Clean up all session storage
      localStorage.removeItem('enhanced_call_state');
      localStorage.removeItem('connection_health');
      localStorage.removeItem('ios_minimal_recovery');
      localStorage.removeItem('call_interrupted_unload');
      
      console.log('Component unmounted - cleaned up all session data');
    };
  }, []);

  // Toggle mute function
  const toggleMute = () => {
    if (!vapi) return;
    try {
      const newMuteState = !isMuted;
      vapi.setMuted(newMuteState);
      setIsMuted(newMuteState);
      
      // Feedback toast for mute status
      toast({
        title: newMuteState ? "Microphone Muted" : "Microphone Active",
        description: newMuteState ? "Your microphone is now muted" : "Your microphone is now active",
        variant: "default"
      });
    } catch (error) {
      console.error("Error toggling mute:", error);
    }
  };

  // Handle retry for token validation
  const handleRetry = () => {
    if (retryCount >= MAX_RETRIES) {
      toast({
        title: "Too Many Attempts",
        description: "Maximum retry attempts reached. Please contact support or try again later.",
        variant: "destructive"
      });
      return;
    }
    
    validateToken(true);
  };

  // Render loading state
  if (isValidating) {
    return (
      <div className="container mx-auto py-12 flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">
          {isRetrying ? "Retrying connection..." : "Validating your check-in link..."}
        </p>
      </div>
    );
  }

  // Render error state for invalid token
  if (!isTokenValid) {
    return (
      <div className="container mx-auto py-8">
        <Card className="p-6 max-w-md mx-auto">
          <h1 className="text-2xl font-bold mb-4">Check-In Link Issue</h1>
          
          {validationError ? (
            <ErrorMessage 
              title={
                validationError.type === ErrorType.TOKEN_EXPIRED ? "Expired Link" :
                validationError.type === ErrorType.TOKEN_INVALID ? "Invalid Link" :
                validationError.type === ErrorType.NETWORK_ERROR ? "Connection Issue" :
                "Error"
              }
              message={validationError.message}
              errorType={validationError.type}
              retryFunction={
                validationError.type === ErrorType.NETWORK_ERROR || 
                validationError.type === ErrorType.API_ERROR ||
                validationError.type === ErrorType.UNKNOWN_ERROR 
                  ? handleRetry 
                  : undefined
              }
              details={validationError.details}
            />
          ) : (
            <ErrorMessage 
              title="Unknown Error" 
              message="An unknown error occurred while validating your check-in link." 
              errorType={ErrorType.UNKNOWN_ERROR}
              retryFunction={handleRetry}
            />
          )}
          
          <div className="mt-6 space-y-4">
            <p className="text-muted-foreground">
              {validationError?.type === ErrorType.TOKEN_EXPIRED || validationError?.type === ErrorType.TOKEN_INVALID 
                ? "Please contact your team administrator to get a new check-in link."
                : "If the problem persists, please contact your team administrator."}
            </p>
            
            <Button 
              className="w-full"
              onClick={() => navigate('/')}
            >
              Return to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Render error state for Vapi initialization errors
  if (validationError?.type === ErrorType.VAPI_ERROR || validationError?.type === ErrorType.MICROPHONE_ERROR) {
    return (
      <div className="container mx-auto py-8">
        <Card className="p-6 max-w-md mx-auto">
          <h1 className="text-2xl font-bold mb-4">Voice Assistant Error</h1>
          
          <ErrorMessage 
            title={validationError.type === ErrorType.MICROPHONE_ERROR ? "Microphone Access Required" : "Voice Assistant Error"}
            message={validationError.message}
            errorType={validationError.type}
            retryFunction={handleRetry}
            details={validationError.details}
          />
          
          <div className="mt-6 space-y-4">
            <p className="text-muted-foreground">
              {validationError.type === ErrorType.MICROPHONE_ERROR 
                ? "This application needs microphone access to work properly. Please ensure you've granted microphone permissions in your browser settings."
                : "There was a problem initializing the voice assistant. Please try again or use a different browser."}
            </p>
            
            <Button 
              className="w-full"
              onClick={() => navigate('/')}
            >
              Return to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <header className="flex flex-col space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Voice Check-In</h1>
        <p className="text-muted-foreground">
          {checkInInfo?.name || "Voice Check-In"}
          {employeeInfo && ` for ${employeeInfo.name}`}
        </p>
      </header>

      {/* Call Guidance Component */}
      <CallGuidance 
        isCallActive={isCallActive}
        wakeLockSupported={wakeLockSupported}
        connectionStrength={connectionState === 'connected' ? 'strong' : 
                          connectionState === 'weak' ? 'weak' : 'disconnected'}
      />

      {/* Reconnection Prompt */}
      <ReconnectionPrompt
        isVisible={showReconnectionPrompt}
        isReconnecting={isReconnecting}
        onReconnect={lightningReconnect}
        onCancel={() => {
          setShowReconnectionPrompt(false);
          localStorage.removeItem('enhanced_call_state');
          localStorage.removeItem('connection_health');
        }}
        estimatedProgress={calculateCallProgress()}
        disconnectionCount={disconnectionCount}
      />

      <Card className="p-4 sm:p-6 shadow-md">
        <div className="flex flex-col space-y-6">
          {/* Voice Activity Indicator */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            {voiceActivity && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-green-600">
                <span className="h-2 w-2 rounded-full bg-green-600 animate-pulse" />
                Speaking
              </span>
            )}
          </div>

          {/* Audio Device Selection */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <Select
              value={selectedAudioDevice}
              onValueChange={setSelectedAudioDevice}
              disabled={isCallActive}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Select microphone" />
              </SelectTrigger>
              <SelectContent>
                {audioDevices
                  .filter(device => device.deviceId && device.deviceId.trim() !== '')
                  .map((device) => (
                    <SelectItem 
                      key={device.deviceId} 
                      value={device.deviceId || 'unknown-device'}
                    >
                      {device.label || `Microphone ${device.deviceId.slice(0, 5)}...`}
                    </SelectItem>
                  ))}
                {audioDevices.filter(device => device.deviceId && device.deviceId.trim() !== '').length === 0 && (
                  <SelectItem value="default">Default Microphone</SelectItem>
                )}
              </SelectContent>
            </Select>
            {micPermissionState === 'granted' && (
              <span title="Microphone access granted">
                <CheckCircle className="h-4 w-4 text-green-500" />
              </span>
            )}
            {micPermissionState === 'denied' && (
              <span title="Microphone access denied">
                <XCircle className="h-4 w-4 text-red-500" />
              </span>
            )}
            {micPermissionState === 'prompt' && (
              <span title="Microphone access required">
                <HelpCircle className="h-4 w-4 text-yellow-500" />
              </span>
            )}
            {micPermissionState === 'unknown' && (
              <span title="Checking microphone permissions...">
                <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
              </span>
            )}
          </div>

          {/* Voice Waveform */}
          <VoiceWave volume={volume} active={isCallActive} isMuted={isMuted} />
          
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              variant={isCallActive ? "outline" : "default"}
              className={cn(
                "flex items-center gap-2 w-full sm:w-auto",
                isCallActive && "border-red-400 text-red-500"
              )}
              onClick={isCallActive ? stopCall : startCall}
              disabled={!assistantConfig?.assistantId || isStarting || micPermissionState === 'denied'}
            >
              {isCallActive ? (
                <>
                  <PhoneOff className="w-5 h-5" /> End Call
                </>
              ) : isStarting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Starting...
                </>
              ) : (
                <>
                  <Phone className="w-5 h-5" /> Start Call
                </>
              )}
            </Button>

            <Button
              size="lg"
              variant="outline"
              className={cn(
                "flex items-center gap-2 w-full sm:w-auto",
                isMuted && "border-amber-400 text-amber-500"
              )}
              onClick={toggleMute}
              disabled={!isCallActive}
            >
              {isMuted ? (
                <>
                  <MicOff className="w-5 h-5" /> Unmute
                </>
              ) : (
                <>
                  <Mic className="w-5 h-5" /> Mute
                </>
              )}
            </Button>
          </div>

          {/* Status */}
          <div className="flex justify-center items-center gap-2">
            {isCallActive ? (
              <>
                <Badge variant="default" className="bg-green-500 hover:bg-green-600">Call Active</Badge>
                {connectionState === 'connected' && (
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    Connected
                  </div>
                )}
                {connectionState === 'weak' && (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                    Weak Signal
                  </div>
                )}
                {connectionState === 'disconnected' && (
                  <div className="flex items-center gap-1 text-xs text-red-600">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    Disconnected
                  </div>
                )}
              </>
            ) : (
              <Badge variant="outline">Call Inactive</Badge>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
} 